import React from "react";
import axios, { AxiosError } from "axios";
import RequestService from "./RequestService";
import AuthenticationService from "./AuthenticationService";

export interface ErrorLogRequest {
  message: string;
  url: string;
  payload: string;
  stack: string;
}

class ErrorLoggingService {
  private static readonly ENDPOINT = "/exception/set";

  private static getBaseUrl(): string {
    return RequestService.getBaseUrl();
  }

  /**
   * Logs an error to the backend
   * @param error The error object or error message
   * @param url The URL where the error occurred
   * @param payload Optional payload/data related to the error
   * @param stack Optional stack trace
   */
  public static async logError(
    error: Error | string | AxiosError,
    url: string = window.location.href,
    payload?: any,
    stack?: string
  ): Promise<void> {
    // Skip logging cancelled requests - these are expected when:
    // - User navigates away from a page
    // - Component unmounts
    // - New request is made to the same endpoint (replacing previous one)
    // - RequestAborter cancels previous requests
    // - Browser cancels requests (e.g., during file downloads when user navigates away)
    if (error && typeof error !== "string" && axios.isCancel(error)) {
      return;
    }

    // Only log errors in production environment
    if (process.env.NODE_ENV !== "production") {
      console.warn(
        "Error logging is disabled in development/staging environment:",
        error
      );
      return;
    }

    try {
      let message: string;
      let errorStack: string;

      if (typeof error === "string") {
        message = error;
        errorStack = stack || "";
      } else if (error instanceof Error) {
        message = error.message || "Unknown error";
        errorStack = error.stack || stack || "";
      } else {
        // AxiosError
        message =
          error.message || error.response?.data?.message || "Request error";
        errorStack = error.stack || stack || "";
      }

      const errorLog: ErrorLogRequest = {
        message,
        url,
        payload: payload ? JSON.stringify(payload) : "",
        stack: errorStack
      };

      // Get authentication token
      const authenticationToken =
        await AuthenticationService.getAuthenticationToken();

      // Build headers with authentication if token is available
      const headers: { Authorization?: string; "Content-Type": string } = {
        "Content-Type": "application/json"
      };

      if (authenticationToken) {
        headers.Authorization = "Bearer " + authenticationToken;
      }

      // Use axios directly to avoid circular dependencies and ensure the error is logged
      // even if RequestService has issues
      await axios
        .post(this.getBaseUrl() + this.ENDPOINT, errorLog, {
          headers,
          timeout: 30000
        })
        .catch((logError) => {
          // Silently fail if logging itself fails to avoid infinite loops
          console.warn("Failed to log error to backend:", logError);
        });
    } catch (e) {
      // Silently fail to avoid infinite loops
      console.warn("Error logging service failed:", e);
    }
  }

  /**
   * Logs a request error (AxiosError)
   * Skips logging for cancelled requests as they are expected behavior
   */
  public static async logRequestError(
    error: AxiosError,
    requestUrl?: string,
    requestPayload?: any
  ): Promise<void> {
    // Skip logging cancelled requests - these are expected when:
    // - User navigates away from a page
    // - Component unmounts
    // - New request is made to the same endpoint (replacing previous one)
    // - RequestAborter cancels previous requests
    if (axios.isCancel(error)) {
      return;
    }

    // Only log errors in production environment
    if (process.env.NODE_ENV !== "production") {
      console.warn(
        "Request error logging is disabled in development/staging environment:",
        error
      );
      return;
    }

    const url = requestUrl || error.config?.url || window.location.href;
    const payload = requestPayload || error.config?.data || {};

    await this.logError(error, url, payload, error.stack);
  }

  /**
   * Logs a React boundary error
   */
  public static async logBoundaryError(
    error: Error,
    errorInfo?: React.ErrorInfo
  ): Promise<void> {
    // Only log errors in production environment
    if (process.env.NODE_ENV !== "production") {
      console.warn(
        "Boundary error logging is disabled in development/staging environment:",
        error
      );
      return;
    }

    const url = window.location.href;
    const payload = errorInfo
      ? {
          componentStack: errorInfo.componentStack,
          errorBoundary: true
        }
      : { errorBoundary: true };

    const stack = error.stack || "";
    await this.logError(error, url, payload, stack);
  }
}

export default ErrorLoggingService;
